import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type QuickBookingMode = 'hour' | 'day' | 'month';

export type QuickBookingDraft = {
  assetId: string;
  mode: QuickBookingMode;
  nonce: number;
  /** List/detail snapshot — used when GET /assets/:id is unavailable during payment. */
  assetSnapshot: Record<string, unknown>;
  createBody: {
    asset_id: string;
    start_date: string;
    end_date: string;
    amount_of_people: number;
    pricing_type_id: number;
    total_price: number;
    addons: [];
  };
};

export type AssetQuickBookRequest = {
  assetId: string;
  assetSnapshot: Record<string, unknown>;
  nonce: number;
};

type BookingCreateContextValue = {
  pendingQuickBooking: QuickBookingDraft | null;
  requestQuickBooking: (draft: QuickBookingDraft) => void;
  clearPendingQuickBooking: () => void;
  /** Open Bookings tab with quick-book UI for an asset (asset page only shows a Book button). */
  pendingAssetQuickBook: AssetQuickBookRequest | null;
  requestAssetQuickBook: (assetId: string, assetSnapshot: Record<string, unknown>) => void;
  clearPendingAssetQuickBook: () => void;
};

const BookingCreateContext = createContext<BookingCreateContextValue | null>(null);

export function BookingCreateProvider({ children }: { children: ReactNode }) {
  const [pendingQuickBooking, setPendingQuickBooking] = useState<QuickBookingDraft | null>(null);
  const [pendingAssetQuickBook, setPendingAssetQuickBook] = useState<AssetQuickBookRequest | null>(
    null,
  );

  const requestQuickBooking = useCallback((draft: QuickBookingDraft) => {
    const id = draft.assetId.trim();
    if (!id) {
      return;
    }
    setPendingQuickBooking({ ...draft, assetId: id, nonce: Date.now() });
  }, []);

  const clearPendingQuickBooking = useCallback(() => {
    setPendingQuickBooking(null);
  }, []);

  const requestAssetQuickBook = useCallback((assetId: string, assetSnapshot: Record<string, unknown>) => {
    const id = assetId.trim();
    if (!id) {
      return;
    }
    setPendingAssetQuickBook({ assetId: id, assetSnapshot, nonce: Date.now() });
  }, []);

  const clearPendingAssetQuickBook = useCallback(() => {
    setPendingAssetQuickBook(null);
  }, []);

  const value = useMemo(
    () => ({
      pendingQuickBooking,
      requestQuickBooking,
      clearPendingQuickBooking,
      pendingAssetQuickBook,
      requestAssetQuickBook,
      clearPendingAssetQuickBook,
    }),
    [
      pendingQuickBooking,
      requestQuickBooking,
      clearPendingQuickBooking,
      pendingAssetQuickBook,
      requestAssetQuickBook,
      clearPendingAssetQuickBook,
    ],
  );

  return <BookingCreateContext.Provider value={value}>{children}</BookingCreateContext.Provider>;
}

export function useBookingCreate(): BookingCreateContextValue {
  const ctx = useContext(BookingCreateContext);
  if (!ctx) {
    throw new Error('useBookingCreate must be used within BookingCreateProvider');
  }
  return ctx;
}
